/**
 * @engine/core - SpriteRenderSystem
 * 
 * Renders all SpriteRenderer components using SpriteBatch.
 * Integrates SpriteRendererStorage with render2d pipeline.
 * 
 * Note: This file uses abstract interfaces to avoid circular dependency
 * with @engine/render2d. The actual implementation is injected at runtime.
 */

import { getSpriteRendererStorage, type SpriteHandle } from './SpriteRendererStorage';
import { getTransformStorage } from './TransformStorage';
import { Profiler } from './Profiler';

/**
 * Texture handle - opaque ID for GPU texture
 */
export type TextureHandle = number;

/**
 * Abstract sprite batch interface
 * Implemented by @engine/render2d SpriteBatch
 */
export interface ISpriteBatch {
  begin(camera: ICamera2D): void;
  end(): void;
  drawQuad(
    texture: TextureHandle,
    x: number, y: number,
    width: number, height: number,
    rotation: number,
    pivotX: number, pivotY: number,
    u0: number, v0: number, u1: number, v1: number,
    color: number
  ): void;
  getDrawCalls?(): number;
  dispose?(): void;
}

/**
 * Abstract camera interface
 */
export interface ICamera2D {
  getViewProjection(out: Float32Array): void;
}

/**
 * Abstract render context interface
 */
export interface IRenderContext {
  backend: string;
}

/**
 * Texture resolver interface
 * Maps asset IDs to GPU texture handles
 */
export interface TextureResolver {
  resolve(assetId: number): TextureHandle | null;
  getSize(assetId: number): { width: number; height: number } | null;
}

/**
 * Sprite render system configuration
 */
export interface SpriteRenderSystemConfig {
  /** Maximum sprites per batch */
  maxSpritesPerBatch?: number;
  /** Enable debug rendering */
  debug?: boolean;
}

/**
 * Per-sprite render data (after transform computation)
 */
interface SpriteRenderData {
  handle: SpriteHandle;
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  width: number;
  height: number;
  pivotX: number;
  pivotY: number;
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  color: number;
  textureId: number;
}

/**
 * SpriteRenderSystem - Renders all sprites efficiently
 * 
 * @example
 * ```typescript
 * const system = new SpriteRenderSystem(ctx, {
 *   maxSpritesPerBatch: 10000,
 * });
 * 
 * // In render loop
 * system.render(camera, textureResolver);
 * ```
 */
export class SpriteRenderSystem {
  private ctx: IRenderContext;
  private batch: ISpriteBatch | null = null;
  private batchFactory: ((ctx: IRenderContext, maxSprites: number) => ISpriteBatch) | null = null;
  private config: Required<SpriteRenderSystemConfig>;
  
  // Stats
  private _lastDrawCalls: number = 0;
  private _lastSpriteCount: number = 0;
  private _lastBatchCount: number = 0;
  
  constructor(ctx: IRenderContext, config: SpriteRenderSystemConfig = {}) {
    this.ctx = ctx;
    this.config = {
      maxSpritesPerBatch: config.maxSpritesPerBatch ?? 10000,
      debug: config.debug ?? false,
    };
  }
  
  /**
   * Set the batch factory (call before render)
   * This allows dependency injection of SpriteBatch from @engine/render2d
   */
  setBatchFactory(factory: (ctx: IRenderContext, maxSprites: number) => ISpriteBatch): void {
    this.batchFactory = factory;
  }
  
  /**
   * Initialize/get sprite batch (lazy creation)
   */
  private getBatch(): ISpriteBatch {
    if (!this.batch && this.batchFactory) {
      this.batch = this.batchFactory(this.ctx, this.config.maxSpritesPerBatch);
    }
    if (!this.batch) {
      throw new Error('SpriteRenderSystem: batch factory not set. Call setBatchFactory() first.');
    }
    return this.batch;
  }
  
  /**
   * Render all sprites
   */
  render(camera: ICamera2D, textureResolver: TextureResolver): void {
    Profiler.begin('SpriteRenderSystem');
    
    const spriteStorage = getSpriteRendererStorage();
    const transformStorage = getTransformStorage();
    const batch = this.getBatch();
    
    // Get z-index from transform for sorting
    const getZIndex = (handle: SpriteHandle): number => {
      // In 2D, use transform's z position as depth
      return transformStorage.getZ(handle);
    };
    
    // Get sorted sprite batch
    Profiler.begin('SpriteRenderSystem.sort');
    const sortedSprites = spriteStorage.getSortedBatch(getZIndex);
    Profiler.end('SpriteRenderSystem.sort');
    
    if (sortedSprites.length === 0) {
      this._lastDrawCalls = 0;
      this._lastSpriteCount = 0;
      this._lastBatchCount = 0;
      Profiler.end('SpriteRenderSystem');
      return;
    }
    
    Profiler.begin('SpriteRenderSystem.batch');
    batch.begin(camera);
    
    let currentTextureHandle: TextureHandle | null = null;
    let spriteCount = 0;
    let batchCount = 0;
    
    for (const entry of sortedSprites) {
      const { handle, textureId } = entry;
      
      // Resolve texture
      const textureHandle = textureResolver.resolve(textureId);
      if (!textureHandle) continue; // Skip if no texture
      
      // Get sprite data
      const data = spriteStorage.getData(handle);
      
      // Get transform
      const worldX = transformStorage.getWorldX(handle);
      const worldY = transformStorage.getWorldY(handle);
      const worldRotation = transformStorage.getWorldRotation(handle);
      const worldScaleX = transformStorage.getWorldScaleX(handle);
      const worldScaleY = transformStorage.getWorldScaleY(handle);
      
      // Determine sprite size
      let width = data.width;
      let height = data.height;
      
      if (width === 0 || height === 0) {
        // Use texture size
        const texSize = textureResolver.getSize(textureId);
        if (texSize) {
          width = texSize.width;
          height = texSize.height;
        } else {
          width = height = 32; // Fallback
        }
      }
      
      // Apply flip
      let u0 = data.u0, v0 = data.v0;
      let u1 = data.u1, v1 = data.v1;
      
      if (data.flipX) {
        const tmp = u0;
        u0 = u1;
        u1 = tmp;
      }
      
      if (data.flipY) {
        const tmp = v0;
        v0 = v1;
        v1 = tmp;
      }
      
      // Draw sprite
      batch.drawQuad(
        textureHandle,
        worldX, worldY,
        width * worldScaleX,
        height * worldScaleY,
        worldRotation,
        data.pivotX, data.pivotY,
        u0, v0, u1, v1,
        data.colorPacked
      );
      
      spriteCount++;
    }
    
    batch.end();
    Profiler.end('SpriteRenderSystem.batch');
    
    // Update stats
    this._lastSpriteCount = spriteCount;
    this._lastDrawCalls = batch.getDrawCalls?.() ?? 0;
    this._lastBatchCount = batchCount;
    
    Profiler.end('SpriteRenderSystem');
  }
  
  /**
   * Get last frame stats
   */
  getStats() {
    return {
      drawCalls: this._lastDrawCalls,
      spriteCount: this._lastSpriteCount,
      batchCount: this._lastBatchCount,
    };
  }
  
  /**
   * Dispose resources
   */
  dispose(): void {
    if (this.batch) {
      this.batch.dispose?.();
      this.batch = null;
    }
  }
}
